import readXlsxFile from "read-excel-file";

// EG-41 — AD-11: leitor tolerante de cabeçalho.
//
// O parser comparava o cabeçalho por igualdade estrita contra estas 14 colunas.
// Acrescentar Pilar e Tema rejeitaria toda planilha já salva pelos gestores, e
// o modelo em si é arquivo estático no S3 que ops sobe quando puder (AD-12).
// Os dois formatos convivem: 14 colunas é o legado, 16 traz classificação.
const COLUNAS_BASE = [
  "Código",
  "Tipo",
  "Anos Anteriores?",
  "Descrição",
  "Helper",
  "Helper Vídeo URL",
  "Alternativas",
  "Pergunta obrigatória?",
  "Evidências em arquivos?",
  "Evidências em fotos/imagens?",
  "Possui SOMA do período?",
  "Possui MÉDIA do período?",
  "Linhas da Matriz",
  "Colunas da Matriz",
];

// GRI-02 — CA05: um TERCEIRO formato, pela mesma razão que existe o segundo.
// Planilha de 16 colunas continua entrando; quem quiser trazer indicador
// acrescenta a 17ª. Os formatos são cumulativos e nesta ordem: não existe
// planilha com GRI e sem as colunas de classificação, porque o cabeçalho é
// comparado por prefixo.
const COLUNAS_DE_CLASSIFICACAO = ["Pilar", "Tema"];
const COLUNA_DE_GRI = ["Indicadores GRI"];

const INDICE_PILAR = COLUNAS_BASE.length;
const INDICE_TEMA = COLUNAS_BASE.length + 1;
const INDICE_GRI = COLUNAS_BASE.length + COLUNAS_DE_CLASSIFICACAO.length;

/**
 * Mesma normalização que o catálogo da EG-40 usa no índice único (unaccent +
 * lower): o gestor digita na planilha, e "governanca" é o mesmo conceito que
 * "Governança".
 */
const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const cabecalhoConhecido = (cabecalho = []) => {
  const base = JSON.stringify(cabecalho.slice(0, COLUNAS_BASE.length));
  if (base !== JSON.stringify(COLUNAS_BASE)) return null;

  if (cabecalho.length === COLUNAS_BASE.length) return "legado";

  const extras = JSON.stringify(cabecalho.slice(COLUNAS_BASE.length));
  if (extras === JSON.stringify(COLUNAS_DE_CLASSIFICACAO)) {
    return "com-classificacao";
  }

  return extras ===
    JSON.stringify([...COLUNAS_DE_CLASSIFICACAO, ...COLUNA_DE_GRI])
    ? "com-gri"
    : null;
};

/**
 * Resolve os indicadores GRI pelos CÓDIGOS, separados por vírgula.
 *
 * Pelo código e não pelo nome: o nome do indicador é uma frase longa
 * ("Emissões diretas de gases de efeito estufa — Escopo 1") que ninguém digita
 * igual duas vezes, enquanto "GRI 305-1" é curto e é o que aparece no relatório.
 *
 * A normalização é a mesma do serviço do GRI-01 (caixa alta, espaço colapsado),
 * então "gri 305-1" e "GRI  305-1" casam com o catálogo.
 *
 * AD-11 vale igual: código que não casa não bloqueia o arquivo. A questão entra
 * sem aquele indicador e o gestor corrige na tela, que agora tem o multiselect.
 */
const resolverGri = (celula, catalogo) => {
  if (!celula || !catalogo?.gri?.length) return [];

  const normalizarCodigo = (texto) =>
    String(texto ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toUpperCase();

  const porCodigo = new Map(
    catalogo.gri.map((item) => [normalizarCodigo(item.code), item.id_gri_indicator])
  );

  const encontrados = String(celula)
    .split(",")
    .map((codigo) => porCodigo.get(normalizarCodigo(codigo)))
    .filter((id) => id != null);

  // Repetir o mesmo código na célula é erro de digitação, não intenção.
  return [...new Set(encontrados)];
};

/**
 * Resolve pilar e tema pelo NOME, contra o catálogo global.
 *
 * AD-11: o que não casa não bloqueia — a questão entra sem classificação e o
 * gestor corrige na tela, que agora tem os dropdowns. Barrar o arquivo inteiro
 * por um nome digitado errado devolveria o gestor à planilha para adivinhar
 * qual linha ofendeu.
 */
const resolverClassificacao = (nomePilar, nomeTema, catalogo) => {
  const vazio = { id_pillar: null, id_theme: null };
  if (!catalogo) return vazio;

  const pilar = (catalogo.pilares || []).find(
    (item) => normalizar(item.name_pillar) === normalizar(nomePilar)
  );

  const tema = (catalogo.temas || []).find(
    (item) => normalizar(item.title) === normalizar(nomeTema)
  );

  if (!tema) return { id_pillar: pilar?.id_pillar ?? null, id_theme: null };

  // Tema de outro pilar formaria um par que o backend recusa no save. Melhor
  // entrar sem tema e deixar o dropdown — que já filtra por pilar — resolver.
  if (pilar && String(tema.id_pillar) !== String(pilar.id_pillar)) {
    return { id_pillar: pilar.id_pillar, id_theme: null };
  }

  return {
    id_pillar: pilar?.id_pillar ?? tema.id_pillar ?? null,
    id_theme: tema.id_theme,
  };
};

export const handleFileChange = async (
  file,
  setTemplateObjects,
  alertError,
  catalogo
) => {
  try {
    const workSheet = await readXlsxFile(file);
    const formato = cabecalhoConhecido(workSheet[0]);

    if (formato) {
      workSheet.shift();
      const newTemplateObjects = workSheet.map((row, index) => {
        let type;
        switch (row[1]) {
          case "Dissertativa":
            type = "text";
            break;
          case "Múltipla Escolha":
            type = "multiple_choice";
            break;
          case "Única Opção":
            type = "multi_choice";
            break;
          case "Numérica":
            type = "number";
            break;
          case "Monetária":
            type = "money";
            break;
          case "Porcentagem":
            type = "percentage";
            break;
          case "Grade/Matriz":
          case "Grid":
            type = "grid";
            break;
          default:
            type = "text";
            break;
        }

        let options = [];
        let gridRows = [];
        let gridColumns = [];

        // Se for tipo grid, processar linhas e colunas da matriz
        if (type === "grid") {
          if (row[12] && row[12].length > 0) {
            gridRows = row[12].split("//").map((rowText, idx) => ({
              description: rowText.trim(),
              row_order: idx,
            }));
          }
          
          if (row[13] && row[13].length > 0) {
            gridColumns = row[13].split("//").map((colText) => ({
              description: colText.trim(),
            }));
          }
        } else {
          // Para outros tipos, processar alternativas normalmente
          if (row[6] && row[6].length > 0) {
            row[6].split("//").forEach((option) => {
              options.push({
                description: option,
              });
            });
          }
        }

        const hasHelper = row[4] || row[5] ? "Sim" : "Não";

        const temClassificacao =
          formato === "com-classificacao" || formato === "com-gri";

        const classificacao = temClassificacao
          ? resolverClassificacao(row[INDICE_PILAR], row[INDICE_TEMA], catalogo)
          : { id_pillar: null, id_theme: null };

        const gri_ids =
          formato === "com-gri" ? resolverGri(row[INDICE_GRI], catalogo) : [];

        return {
          ...classificacao,
          gri_ids,
          code: row[0],
          title: row[3],
          type: type,
          previousYears: row[2] || "Não",
          isRequired: row[7] || "Não",
          requestFiles: row[8] || "Não",
          requestImages: row[9] || "Não",
          hasHelper: hasHelper,
          hasSum: row[10] || "Não",
          hasAverage: row[11] || "Não",
          helper: {
            description: row[4] || "",
            url_video: row[5] || "",
          },
          options: options,
          gridRows: gridRows,
          gridColumns: gridColumns,
        };
      });

      setTemplateObjects(newTemplateObjects);
    } else {
      alertError(
        `Erro no arquivo XLSX, verifique o cabeçalho. Colunas esperadas: ${COLUNAS_BASE.map(
          (coluna) => `'${coluna}'`
        ).join(", ")}. Opcionalmente, acrescente ${COLUNAS_DE_CLASSIFICACAO.map(
          (coluna) => `'${coluna}'`
        ).join(
          " e "
        )} ao final para importar a classificação, e '${COLUNA_DE_GRI}' depois delas para importar os indicadores GRI.`
      );
    }
  } catch (error) {
    alertError("Erro ao processar o arquivo.");
  }
};
